import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { fetchSiteById } from "../redux/slices/siteSlice";
import {
  fetchSiteBackups,
  clearBackupState,
} from "../redux/slices/backupSlice";
import { fetchSftpUsers } from "../redux/slices/sftpSlice";
import { useSocket } from "../contexts/SocketContext";

/**
 * Custom hook for managing site detail data fetching and socket events
 */
export const useSiteDetail = ({ siteId, onShowMessage }) => {
  const dispatch = useDispatch();
  const { socket } = useSocket();

  // Fetch site data on mount
  useEffect(() => {
    dispatch(fetchSiteById(siteId));
    dispatch(fetchSftpUsers(siteId));
    dispatch(fetchSiteBackups({ siteId }));
  }, [dispatch, siteId]);

  // Clear backup state on unmount
  useEffect(() => {
    return () => {
      dispatch(clearBackupState());
    };
  }, [dispatch]);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    const handleRestoreStatus = (data) => {
      if (data.siteId !== siteId) return;

      switch (data.status) {
        case "pending":
          onShowMessage("Restore initiated...");
          break;
        case "in_progress":
          onShowMessage(data.message || "Restore in progress...");
          break;
        case "completed":
          onShowMessage("Site restored successfully!");
          dispatch(fetchSiteById(siteId));
          dispatch(fetchSiteBackups({ siteId }));
          break;
        case "failed":
          onShowMessage(`Restore failed: ${data.error || "Unknown error"}`);
          break;
        default:
          break;
      }
    };

    const handleSiteRestored = (data) => {
      if (data.siteId === siteId) {
        onShowMessage(`Site "${data.siteName}" restored successfully!`);
        dispatch(fetchSiteById(siteId));
      }
    };

    const handleSiteUpdateProgress = (data) => {
      if (data.siteId !== siteId) return;

      switch (data.status) {
        case "UPDATING":
          onShowMessage(data.message || "Site update in progress...");
          break;
        case "COMPLETED":
          onShowMessage("Site updated successfully!");
          // Refetch site data to get updated information
          dispatch(fetchSiteById(siteId));
          break;
        case "FAILED":
          onShowMessage(`Update failed: ${data.error || data.message}`);
          // Refetch site data to restore original state
          dispatch(fetchSiteById(siteId));
          break;
        default:
          break;
      }
    };

    const handleBackupStatus = (data) => {
      if (data.siteId !== siteId) return;

      switch (data.status) {
        case "pending":
          onShowMessage("Backup initiated...");
          break;
        case "in_progress":
          onShowMessage(data.message || "Creating backup...");
          break;
        case "completed":
          onShowMessage(
            `Backup completed successfully! Size: ${data.data?.sizeMB || 0} MB`,
          );
          // Refetch backups list to show new backup
          dispatch(fetchSiteBackups({ siteId }));
          break;
        case "failed":
          onShowMessage(`Backup failed: ${data.error || "Unknown error"}`);
          // Refetch backups list to update failed backup status
          dispatch(fetchSiteBackups({ siteId }));
          break;
        default:
          break;
      }
    };

    socket.on("restore_status", handleRestoreStatus);
    socket.on("site_restored", handleSiteRestored);
    socket.on("siteUpdateProgress", handleSiteUpdateProgress);
    socket.on("backup_status", handleBackupStatus);

    return () => {
      socket.off("restore_status", handleRestoreStatus);
      socket.off("site_restored", handleSiteRestored);
      socket.off("siteUpdateProgress", handleSiteUpdateProgress);
      socket.off("backup_status", handleBackupStatus);
    };
  }, [socket, siteId, dispatch, onShowMessage]);
};
